import { createFileRoute } from "@tanstack/react-router";
import { PageDatesBadge } from "@/components/site/PageDatesBadge";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section } from "@/components/site/Blocks";
import { LicensingDisclosure } from "@/components/site/LicensingDisclosure";
import { SITE } from "@/data/site";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

const URL = "/legal/editorial-standards";

export const Route = createFileRoute("/legal/editorial-standards")({
  head: () =>
    buildHead({
      title: "Editorial Standards | Simply Approved Mortgages",
      description:
        "How we research, write, review, and correct DSCR and business-purpose investment lending content, including our use of AI-assisted drafts.",
      url: URL,
      keywords:
        "editorial standards, content review policy, DSCR content accuracy, AI content review, mortgage content corrections policy",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Legal", url: "/legal" },
        { name: "Editorial Standards", url: URL },
      ],
      localBusiness: false,
    }),
  component: Page,
});

function Page() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="Editorial standards"
        title="How we create and review investor lending content"
        subtitle="Our source hierarchy, compliance review, corrections workflow, and use of AI-assisted drafts."
      />
      <PageEssentials />
      <Section>
        <div className="prose prose-slate mx-auto max-w-3xl">
          <PageDatesBadge className="not-prose mb-4" />

          <h2>Our editorial purpose</h2>
          <p>
            {SITE.brand} publishes plain-English education about DSCR loans, rental property
            financing, and other business-purpose investment programs. Pages should answer the
            investor's main question near the top, explain the real trade-offs, show the math, and
            give a clear next step without pressure or unsupported promises.
          </p>

          <h2>Source hierarchy</h2>
          <ol>
            <li>
              Primary government and regulatory sources — HUD (Fair Market Rents, income limits,
              census-tract designations), Census, BLS, CFPB, and state regulators.
            </li>
            <li>Published lender program guidelines and industry data for program mechanics.</li>
            <li>Reputable market data providers for rent, value, and market-level statistics.</li>
          </ol>
          <p>
            We cite the source next to material statistics, limits, or requirements, and we show the
            data vintage where it matters. Wholesale lender identities and proprietary rate sheets
            are never published.
          </p>

          <h2>Compliance review</h2>
          <p>
            Before a material page is published or materially changed, it is checked against our
            advertising-compliance rules: Regulation N (mortgage acts and practices), Regulation Z
            triggering terms, ECOA and Fair Housing, UDAAP, and applicable state advertising rules.
            We do not describe ourselves as a lender, we do not present an estimate as an approval
            or commitment, and we do not publish leverage, credit, or loan-size claims that exceed
            our actual program limits.
          </p>

          <h2>Rate, pricing, and calculator claims</h2>
          <p>
            Any rate or payment shown must come from our audited pricing pipeline with its
            assumptions and timestamp displayed. Payments are principal and interest only. Calculator
            outputs are labeled as illustrative estimates, not quotes. See our{" "}
            <a href="/legal/calculator-methodology">calculator methodology</a> for the underlying
            models.
          </p>

          <h2>AI-assisted drafts</h2>
          <p>
            AI may help organize research or prepare a draft, but it is not the source of truth.
            AI-assisted content is held for human review and may not invent rates, statistics,
            citations, credentials, testimonials, government relationships, local facts, or program
            rules. Blog posts and social drafts remain unpublished until approved by a human
            reviewer.
          </p>

          <h2>Updates and corrections</h2>
          <p>
            We review time-sensitive content on a recurring schedule and whenever an authoritative
            source changes. A "last updated" date is not changed merely to make a page look fresh.
            If you find an error, contact us and we will investigate, correct the page, and keep an
            internal record of the change.
          </p>

          <h2>What a final review checks</h2>
          <ul>
            <li>Search intent, standalone value, originality, and a useful next action.</li>
            <li>Accurate claims, authoritative citations, and current data vintages.</li>
            <li>Broker identity, licensing scope, disclosures, and advertising safeguards.</li>
            <li>Readable structure, accessible controls, mobile layout, internal links, valid schema.</li>
            <li>Calculator assumptions, limitations, and the difference between an estimate and a loan decision.</li>
          </ul>

          <h2>Human review is not a guarantee of accuracy</h2>
          <p>
            Our review process is a good-faith effort to publish accurate, useful, compliant
            content. It is not a warranty. The fact that a page, blog post, calculator explanation,
            or AI-assisted draft was reviewed by a person does not mean it is accurate, complete,
            current, or applicable to your property, entity, state, or lender. Guidelines, pricing,
            tax rules, and local regulations change constantly, third-party data can be wrong, and
            AI-assisted drafts can contain confident-sounding errors that survive review. Content on
            this site is educational only — it is never a quote, an approval, an offer of credit, or
            legal, tax, accounting, or investment advice.
          </p>

          <h2>Use of this content is at your own risk</h2>
          <p>
            All editorial content is provided "as is," without warranties of any kind. You should
            independently verify every material figure, guideline, and deadline with a licensed loan
            officer and your own legal, tax, and financial advisers before acting. To the maximum
            extent permitted by law, {SITE.brand} and its officers, employees, agents, and
            affiliates are not liable for any loss or damage — including lost profits, lost
            opportunity, transaction costs, or investment losses — arising from your use of, or
            reliance on, anything published here, whether or not it was human-reviewed. See our{" "}
            <a href="/legal/disclaimers">disclaimers</a> and{" "}
            <a href="/legal/terms">terms of use</a>, which govern your use of this site.
          </p>

          <h2>Contact</h2>
          <p>
            Questions or a correction request? Call <a href={SITE.phoneHref}>{SITE.phone}</a> or use
            our <a href="/contact">contact form</a>.
          </p>

          <LicensingDisclosure className="not-prose mt-6 text-sm text-muted-foreground" />
        </div>
      </Section>
    <PageOutro />
      </SiteShell>
  );
}
